import { TableFilter, TTableFilter } from '@components/table/types';
import { QuoteDataGridType, UnderwriterHomePageTabs } from '@pages/Quotes/QuotesTable/DataGrid.constants';
import { QuoteColumnField } from '@pages/Quotes/QuotesTable/QuoteColumnField.enum';

export const getQuoteFilters = ({
  viewType,
  isInternalUser,
}: {
  viewType: QuoteDataGridType;
  isInternalUser: boolean;
}): TTableFilter[] => {
  const items: (TTableFilter & { isVisible?: boolean })[] = [
    {
      field: QuoteColumnField.ProductOffering,
      label: 'Product',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.OFFERING_TYPEAHEAD,
      childrenFields: [QuoteColumnField.BusinessClass, QuoteColumnField.ProductLabel],
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
    },
    {
      field: QuoteColumnField.BusinessClass,
      label: 'Business Class',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.BUSINESS_CLASS_TYPEAHEAD,
      watchedField: QuoteColumnField.ProductOffering,
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
    },
    {
      field: QuoteColumnField.ProductLabel,
      label: 'Product Label',
      width: { gridWidth: { xs: 12 } },
      watchedField: QuoteColumnField.ProductOffering,
      type: TableFilter.PRODUCT_LABEL_TYPEAHEAD,
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
    },
    {
      field: QuoteColumnField.TransactionType,
      label: 'Transaction Type',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.TRANSACTION_TYPE_TYPEAHEAD,
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
      isVisible: [QuoteDataGridType.UWHomePageAllTransaction].includes(viewType),
    },
    {
      field: QuoteColumnField.Status,
      label: 'Quote Status',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.QUOTE_STATUS_TYPEAHEAD,
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
      isVisible: UnderwriterHomePageTabs.includes(viewType),
    },
    {
      field: QuoteColumnField.UnderwritingActivityType,
      label: 'Underwriting Activity',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.UNDERWRITING_ACTIVITY_TYPE_TYPEAHEAD,
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
      isVisible: isInternalUser,
    },
    {
      field: QuoteColumnField.DuplicateQuotes,
      label: 'Duplicate Quote',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.CHECKBOX,
      fieldConfig: {
        selectField: {
          multiSelect: false,
        },
      },
    },
    {
      field: QuoteColumnField.EffectiveDate,
      label: 'Effective Date',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.DATE_RANGE,
      fieldConfig: {
        dateRangeField: {
          displayMode: 'horizontal',
        },
      },
    },
    {
      field: QuoteColumnField.ModifiedDate,
      label: 'Modified Date',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.DATE_RANGE,
      fieldConfig: {
        dateRangeField: {
          displayMode: 'horizontal',
        },
      },
    },
    {
      field: QuoteColumnField.StatusChangeDate,
      label: 'Status Change Date',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.DATE_RANGE,
      fieldConfig: {
        dateRangeField: {
          displayMode: 'horizontal',
        },
      },
    },
    {
      field: QuoteColumnField.Jurisdiction,
      label: 'Jurisdiction',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.JURISDICTION_TYPEAHEAD,
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
    },
    {
      field: QuoteColumnField.TotalPremium,
      label: 'Total Premium',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.NUMBER_RANGE,
      fieldConfig: {
        textField: {
          type: 'number',
          inputMode: 'numeric',
        },
      },
    },
    {
      field: QuoteColumnField.Underwriter,
      label: 'Underwriter',
      helperText:
        viewType === QuoteDataGridType.UWHomePageAllTransaction ? "Only meant to be used with 'All'" : undefined,
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.UNDERWRITER_TYPEAHEAD_FILTER,
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
    },
    {
      field: QuoteColumnField.Brokerage,
      label: 'Brokerage',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.BROKERAGE_TYPEAHEAD,
      childrenFields: [QuoteColumnField.Producer],
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
    },
    {
      field: QuoteColumnField.Producer,
      label: 'Producer',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.BROKERAGE_PRODUCER_TYPEAHEAD,
      watchedField: QuoteColumnField.Brokerage,
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
    },
    {
      field: QuoteColumnField.CreatedByUser,
      label: 'Created By User',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.TEXT_FIELD,
    },
    {
      field: QuoteColumnField.QuoteSource,
      label: 'Quote Source',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.REPORTING_QUOTE_SOURCE_TYPEAHEAD,
      fieldConfig: {
        selectField: {
          multiSelect: true,
        },
      },
      isVisible:
        (viewType === QuoteDataGridType.QuoteTypeNewBusiness || UnderwriterHomePageTabs.includes(viewType)) &&
        isInternalUser,
    },
    {
      field: QuoteColumnField.QuoteOrigin,
      label: 'Quote Origin',
      width: { gridWidth: { xs: 12 } },
      type: TableFilter.QUOTE_ORIGIN_TYPEAHEAD,
      fieldConfig: {
        selectField: {
          multiSelect: false,
        },
      },
      isVisible:
        (viewType === QuoteDataGridType.QuoteTypeNewBusiness || UnderwriterHomePageTabs.includes(viewType)) &&
        isInternalUser,
    },
  ];

  return items.filter((x) => x.isVisible !== false);
};
